import some from 'lodash-es/some';

import type { Facet } from '@jane/bloom/types/facet';
import type { ProductSearchCriteria } from '@jane/bloom/viewModels/productSearch';
import type {
  PriceSearchFilterFacet,
  RatingSearchFilterFacet,
} from '@jane/bloom/viewModels/productSearchFilters';

export const getUpdatedCountsRating = (
  filterItems: RatingSearchFilterFacet[],
  currentFacet?: Facet
) => {
  filterItems.forEach((item) => {
    const count =
      currentFacet &&
      Object.entries(currentFacet)
        .filter(([rating]) => parseFloat(rating) >= item.start)
        .reduce((acc, [, value]) => acc + value, 0);

    item.count = count || 0;
  });
  return filterItems;
};

export const getUpdatedCountsPrice = (
  filterItems: PriceSearchFilterFacet[],
  currentFacet?: Facet
) => {
  filterItems.forEach((item) => {
    let count = 0;

    if (item.start && item.end) {
      count =
        (currentFacet &&
          Object.entries(currentFacet)
            .filter(
              ([price]) =>
                // Blank `start` was filtered out earlier
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                parseFloat(price) >= item.start! &&
                // Blank `end` was filtered out earlier
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                parseFloat(price) <= item.end!
            )
            .reduce((acc, [, value]) => acc + value, 0)) ||
        0;
    } else if (item.start) {
      count =
        (currentFacet &&
          Object.entries(currentFacet)
            // Blank `start` was filtered out earlier
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            .filter(([price]) => parseFloat(price) >= item.start!)
            .reduce((acc, [, value]) => acc + value, 0)) ||
        0;
    } else if (item.end) {
      count =
        (currentFacet &&
          Object.entries(currentFacet)
            // Blank `end` was filtered out earlier
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            .filter(([price]) => parseFloat(price) <= item.end!)
            .reduce((acc, [, value]) => acc + value, 0)) ||
        0;
    } else {
      count =
        (currentFacet &&
          Object.values(currentFacet).reduce((acc, value) => acc + value, 0)) ||
        0;
    }

    item.count = count;
  });
  return filterItems;
};

/**
 * Maps Bloom filter names to Jane DM facet names and checks if they're active
 * This 3-layer naming system exists because:
 * - URLs use user-friendly names (?lineage=indica)
 * - Bloom uses logical plurals (lineages: ['indica'])
 * - Jane DM service has its own facet schema
 */
export const extractActiveFilters = (
  searchCriteria?: ProductSearchCriteria
): { [facetGroup: string]: boolean } => {
  if (!searchCriteria) return {};

  return {
    activities: some(searchCriteria.activities),
    aggregate_rating: some(searchCriteria.ratings), // Ratings → aggregate_rating facet
    applicable_special_ids: some(searchCriteria.specialIds),
    available_weights: some(searchCriteria.weights),
    brand: some(searchCriteria.brands), // Note: singular in DM facets, plural in Bloom
    bucket_price: some(searchCriteria.prices), // Price ranges → bucket_price facet
    category: some(searchCriteria.lineages), // Lineages (Indica, Sativa) → category facet
    compound_names: some(searchCriteria.terpenes), // Terpenes → compound_names facet
    feelings: some(searchCriteria.feelings),
    has_brand_discount: !!searchCriteria.hasBrandDiscount,
    percent_cbd: Boolean(
      typeof searchCriteria.cbdPotenciesMax === 'number' ||
      typeof searchCriteria.cbdPotenciesMin === 'number'
    ),
    percent_thc: Boolean(
      typeof searchCriteria.thcPotenciesMax === 'number' ||
      typeof searchCriteria.thcPotenciesMin === 'number'
    ),
    root_types: some(searchCriteria.categories), // Categories (Edibles, Flower) → root_types facet
  };
};

export const hasAnyFiltersApplied = (
  searchCriteria?: ProductSearchCriteria
): boolean => {
  const activeFilters = extractActiveFilters(searchCriteria);
  return Object.values(activeFilters).some(Boolean);
};
interface BlendFacetArgs {
  baselineFacet?: Facet;
  currentFacetGroup?: string;
  filteredFacet?: Facet;
  frozenFacetStateClient?: Facet;
  isFilteringThisFacet?: boolean;
  setLocalStorageCallback?: (
    facetGroup: string,
    facetData: Record<string, number>
  ) => void;
}

/**
 * Combines two sets of DM facets to determine what options are available for filtering:
 * - The set of facets for the current PLP, if no extra filters are applied.
 * - The set of facets from the current search criteria.
 *
 * Desired filter behavior (same as in ecomm):
 * 1. The filter options counts (e.g. Flower (50)) should be coming from DM facets.
 * 2. Once a filter option is selected, the counts for other filter options should be updated to reflect what the user can filter while still getting results (i.e. refetch facets)
 * 3. When a filter option produces 0 search results, the option should be disabled rather than removed from the filter.
 * 4. When a filter option is selected from a dropdown (e.g. Sativa from the Lineage dropdown), the counts for other filter options in that dropdown should not be updated and should still be selectable.
 */
export const blendFacet = ({
  baselineFacet = {},
  currentFacetGroup,
  filteredFacet = {},
  frozenFacetStateClient,
  isFilteringThisFacet,
  setLocalStorageCallback,
}: BlendFacetArgs = {}) => {
  // Start with all baseline keys set to 0
  const result = Object.fromEntries(
    Object.keys(baselineFacet).map((key) => [key, 0])
  );

  if (!currentFacetGroup) {
    return {
      ...result,
      ...(isFilteringThisFacet ? baselineFacet : filteredFacet),
    };
  }

  // For active filter groups: Use frozen state from localStorage
  if (isFilteringThisFacet && currentFacetGroup) {
    if (frozenFacetStateClient) {
      return {
        ...result,
        ...frozenFacetStateClient,
      };
    }

    // Fallback to baseline if no preserved state (shouldn't happen in normal flow)
    return {
      ...result,
      ...baselineFacet,
    };
  }

  // For inactive filter groups: Use filtered counts and preserve this state
  if (currentFacetGroup) {
    for (const filter in filteredFacet) {
      if (filter in baselineFacet) {
        result[filter] = filteredFacet[filter];
      }
    }
  }

  if (setLocalStorageCallback) {
    setLocalStorageCallback(currentFacetGroup, result);
    return result;
  }

  // Fallback: Use filtered counts
  for (const filter in filteredFacet) {
    if (filter in baselineFacet) {
      result[filter] = filteredFacet[filter];
    }
  }

  return result;
};
